import gsap from 'gsap'
import { Component, EventBus } from '../core/smpl-components.js'
import { Events } from '../core/events.js'
import { MqlMatchMedia } from '../utils/mql-match-media.js'

// -----------------------------------------------------------------------------
// Animation tuning. Everything that moves is configured here — the stylesheet
// only does layout, stacking and colour, so there is one place to reach for when
// the feel is off.
// -----------------------------------------------------------------------------

// Geometry, per breakpoint. Distances in px, scale as a factor. On mobile the
// cards are already staggered by the grid (_header-panels.scss), so the parked
// one keeps its place and size and is only blurred: a swap changes which card
// is in front.
const SETTINGS = {
	base: {
		scaleBack: 0.88, // scale of the parked card
		titleBack: 0.65, // extra scale on the parked card's title, on top of scaleBack
		insetCols: 1, // gap left beside the parked card, in grid columns
		arcDist: 156, // how far the incoming card swings out on its way round
		blur: 30, // blur on the parked card
	},
	mobile: {
		scaleBack: 1,
		titleBack: 1,
		insetCols: 0,
		arcDist: 50,
		blur: 10,
	},
}

const TIMING = { swap: 0.5, hover: 0.3 }
const EASE = { swap: 'power2.inOut', hover: 'power2.out' }

// A swipe switches the cards: at least this far sideways (px), and clearly more
// sideways than up or down, so a vertical scroll over the cards never swaps them.
const SWIPE = { distance: 40, ratio: 1.5 }

// How far towards the front a hovered parked card leans (0–1).
const HOVER_LEAN = 0.18

// Fraction of the swap after which the incoming card holds the front. The swap
// ease is symmetric, so this is both the halfway point in time and the apex of
// the swing.
const ARC_PEAK = 0.5

// Fraction of the swap over which the blur clears — shorter than the move, so
// the card comes into focus on the way in rather than at the last moment.
const BLUR_CLEAR = 0.7

// Outward swing as a function of progress: zero at both ends, peaking mid-move.
// Squaring the sine matters — plain sin(pi t) is zero in value at the ends but
// not in slope, so the swing would still be travelling sideways at full speed
// when the tween cuts off, and the move would land with a jolt. sin^2 arrives at
// both ends with zero velocity, so the swing fades into the travel's own easing.
// Peak is 1.0 either way, so `arcDist` is unaffected.
const arcShape = (t) => Math.sin(t * Math.PI) ** 2

// -----------------------------------------------------------------------------
// Header panels.
//
// Two cards overlapping around the centre column line, one in front at a time.
// Clicking the parked card sends it out and around the other one to take the
// front; a sideways swipe over either card does the same. Hovering it leans it
// forward as an affordance. Position, scale and blur
// are all owned here via GSAP — the stylesheet carries no transitions.
// Selector: `.js-header-panels` (see setup/components.js).
// -----------------------------------------------------------------------------
class HeaderPanels {
	constructor(options) {
		this.element = options.el

		const panels = Array.from(this.element.querySelectorAll('.js-header-panel'))
		if (panels.length < 2) return

		this.items = panels.map((panel) => {
			// Which side a card sits on decides which way it scales and swings:
			// the origin is its *inner* edge, so scaling back pins that edge to
			// the centre line and pulls the outer edge in.
			const dir = panel.classList.contains('header-panels__panel--end') ? 1 : -1
			const inner = panel.querySelector('.header-panel__inner')

			// Titles shrink towards their top-left corner, where the content padding pins them.
			const titles = panel.querySelectorAll('.header-panel__title')

			gsap.set(inner, { transformOrigin: dir === 1 ? '0% 50%' : '100% 50%' })
			gsap.set(titles, { transformOrigin: '0% 0%' })

			return {
				dir,
				panel,
				inner,
				toggle: panel.querySelector('.js-header-panel-toggle'),
				content: panel.querySelector('.header-panel__content'),
				titles,
				// Only the front card shows its footer (the call to action).
				footers: panel.querySelectorAll('.header-panel__content__footer'),
				// Blurred separately from __inner so the toggle's focus ring stays sharp.
				// A berg-char directly in __inner (the hands) hangs outside the surface's
				// clip, so it is blurred alongside it.
				blurTargets: panel.querySelectorAll('.header-panel__surface, .header-panel__inner > .berg-char'),
			}
		})

		this.targets = this.items.flatMap((item) => [item.inner, ...item.blurTargets, ...item.titles, ...item.footers])
		this.canHover = window.matchMedia('(hover: hover)').matches

		// Fires immediately, so this also seeds `this.settings` before anything is placed.
		this.matchMedia = new MqlMatchMedia(`(max-width:${window.app.breakpoints.MEDIUM - 1}px)`, (isMobile) => {
			this.settings = isMobile ? SETTINGS.mobile : SETTINGS.base
			this.refresh()
		})

		this.bindEvents()

		// The active card is rendered server-side, so read it rather than assume 0.
		const initial = this.items.findIndex((item) => item.panel.classList.contains('is-active'))
		this.setActive(initial === -1 ? 0 : initial)
	}

	bindEvents() {
		this.bndOnClick = this.onClick.bind(this)
		this.bndOnKeydown = this.onKeydown.bind(this)
		this.bndOnHover = this.onHover.bind(this)
		this.bndOnTouchStart = this.onTouchStart.bind(this)
		this.bndOnTouchEnd = this.onTouchEnd.bind(this)

		this.element.addEventListener('click', this.bndOnClick)
		this.element.addEventListener('keydown', this.bndOnKeydown)
		// Passive: only read, so the page keeps scrolling vertically underneath.
		this.element.addEventListener('touchstart', this.bndOnTouchStart, { passive: true })
		this.element.addEventListener('touchend', this.bndOnTouchEnd, { passive: true })
		this.items.forEach(({ panel }) => {
			panel.addEventListener('mouseenter', this.bndOnHover)
			panel.addEventListener('mouseleave', this.bndOnHover)
		})

		// The offset is measured off the grid, so it has to be re-taken when the
		// container changes width — not just when the breakpoint flips.
		this.bndOnResize = this.refresh.bind(this)
		EventBus.on(Events.PAGE_RESIZE, this.bndOnResize)
	}

	unbindEvents() {
		this.element.removeEventListener('click', this.bndOnClick)
		this.element.removeEventListener('keydown', this.bndOnKeydown)
		this.element.removeEventListener('touchstart', this.bndOnTouchStart)
		this.element.removeEventListener('touchend', this.bndOnTouchEnd)
		this.items.forEach(({ panel }) => {
			panel.removeEventListener('mouseenter', this.bndOnHover)
			panel.removeEventListener('mouseleave', this.bndOnHover)
		})

		EventBus.off(Events.PAGE_RESIZE, this.bndOnResize)
	}

	onClick(event) {
		const toggle = event.target.closest('.js-header-panel-toggle')
		if (!toggle) return

		const index = this.items.findIndex((item) => item.panel.contains(toggle))
		if (index !== -1) this.setActive(index)
	}

	onKeydown(event) {
		if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
		if (!event.target.closest('.js-header-panel-toggle')) return

		event.preventDefault()
		this.setActive(event.key === 'ArrowLeft' ? 0 : this.items.length - 1)
	}

	onTouchStart(event) {
		const touch = event.touches.length === 1 ? event.touches[0] : null
		this.touchStart = touch && { x: touch.clientX, y: touch.clientY }
	}

	// Either direction swaps: there are only two cards, so a swipe the "wrong"
	// way doing nothing would just feel broken.
	onTouchEnd(event) {
		if (!this.touchStart) return

		const touch = event.changedTouches[0]
		const dx = Math.abs(touch.clientX - this.touchStart.x)
		const dy = Math.abs(touch.clientY - this.touchStart.y)
		this.touchStart = null

		if (dx < SWIPE.distance || dx < dy * SWIPE.ratio) return

		this.setActive(this.activeIndex === 0 ? 1 : 0)
	}

	onHover(event) {
		const index = this.items.findIndex((item) => item.panel === event.currentTarget)

		// Pointer devices only; a swap in flight owns the cards and must not be fought.
		if (!this.canHover || index === -1 || index === this.activeIndex || this.swap) return

		this.settle(index, {
			lean: event.type === 'mouseenter' ? HOVER_LEAN : 0,
			duration: TIMING.hover,
		})
	}

	setActive(index) {
		if (index === this.activeIndex) return

		// No animation for the very first placement, or when motion is not wanted.
		const immediate = this.activeIndex === undefined || window.matchMedia('(prefers-reduced-motion: reduce)').matches
		const previous = this.activeIndex

		this.activeIndex = index
		this.stop()

		// Semantic state flips straight away, so a click registers instantly for
		// assistive tech even though the card takes another half second to arrive.
		this.applyState(index)

		if (immediate) {
			this.setFront(index)
			this.items.forEach((_, i) => this.settle(i))
			return
		}

		// The outgoing card simply recedes to its parked position…
		this.settle(previous, { duration: TIMING.swap, ease: EASE.swap })

		// …while the incoming one travels in and, on top of that travel, swings
		// out and back. Riding the swing as a modifier keeps it one tween — and so
		// one continuous velocity curve — rather than two that sum into a kink.
		const item = this.items[index]
		const { arcDist } = this.settings

		const swap = gsap.to(item.inner, {
			x: 0,
			scale: 1,
			duration: TIMING.swap,
			ease: EASE.swap,
			modifiers: {
				// x arrives as a px string, so parse before adding the swing and
				// hand back a string GSAP can read.
				x: (x) => `${parseFloat(x) + item.dir * arcDist * arcShape(swap.progress())}px`,
			},
			onUpdate: () => {
				// Handed over at the apex, while the incoming card is clear of the
				// overlap — that is what stops it cutting through the other one.
				if (swap.progress() >= ARC_PEAK) this.setFront(index)
			},
			onComplete: () => {
				this.swap = null
			},
		})

		this.swap = swap
		gsap.to(item.blurTargets, { filter: 'blur(0px)', duration: TIMING.swap * BLUR_CLEAR, ease: EASE.swap })
		gsap.to(item.titles, { scale: 1, duration: TIMING.swap, ease: EASE.swap })
		// Revealed once the card has taken the front, so it never shows through the other one.
		gsap.to(item.footers, {
			autoAlpha: 1,
			delay: TIMING.swap * ARC_PEAK,
			duration: TIMING.swap * (1 - ARC_PEAK),
			ease: EASE.hover,
		})
	}

	// Puts a card where it belongs when nothing is swapping — instantly by
	// default, or eased over `duration`. `lean` pulls a parked card part-way
	// towards the front.
	settle(index, { lean = 0, duration = 0, ease = EASE.hover } = {}) {
		const item = this.items[index]
		const { scaleBack, titleBack, insetCols, blur } = this.settings
		const p = index === this.activeIndex ? 1 : lean
		const method = duration ? 'to' : 'set'

		// Scaling about the inner edge already pulls the outer edge inward; shift by
		// whatever is left over so it lands exactly one column inside the container,
		// on a grid line. The two work against each other, so this is a correction
		// rather than an offset — it can come out either way depending on the scale.
		const scale = 1 - (1 - scaleBack) * (1 - p)
		const inset = this.columnStep * insetCols - (1 - scale) * item.width

		gsap[method](item.inner, {
			x: -item.dir * inset * (1 - p),
			scale,
			duration,
			ease,
		})
		gsap[method](item.blurTargets, { filter: `blur(${blur * (1 - p)}px)`, duration, ease })
		gsap[method](item.titles, { scale: 1 - (1 - titleBack) * (1 - p), duration, ease })
		// Hidden on any parked card, leaning or not; a hover lean only hints at the front.
		gsap[method](item.footers, { autoAlpha: index === this.activeIndex ? 1 : 0, duration, ease })
	}

	// One grid column plus its gutter, read off the live grid. Computed style resolves
	// grid-template-columns to used px values, so this stays correct at any container
	// width without the stylesheet having to expose the number in a readable unit.
	measure() {
		const styles = getComputedStyle(this.element)
		const gap = parseFloat(styles.columnGap) || 0

		this.columnStep = parseFloat(styles.gridTemplateColumns) + gap
		// Layout width, so it is unaffected by whatever transform is on the card.
		this.items.forEach((item) => {
			item.width = item.inner.offsetWidth
		})
	}

	// Re-measure and re-seat the parked cards. A swap in flight owns them, so it is
	// left to finish — the next settle picks up the new measurement.
	refresh() {
		this.measure()
		if (this.swap) return

		this.items.forEach((_, i) => this.settle(i))
	}

	// Stacking only, handed over mid-swap.
	setFront(index) {
		this.items.forEach((item, i) => item.panel.classList.toggle('is-front', i === index))
	}

	applyState(index) {
		this.items.forEach((item, i) => {
			const isActive = i === index

			item.panel.classList.toggle('is-active', isActive)
			// Disabled in front so the card's own links are reachable through the overlay.
			item.toggle.disabled = isActive
			item.toggle.setAttribute('aria-pressed', String(isActive))
			// Keep focus out of the blurred card sitting behind the other one.
			item.content.inert = !isActive
		})
	}

	stop() {
		gsap.killTweensOf(this.targets)
		this.swap = null
	}

	destroy() {
		if (!this.items) return

		this.stop()
		this.unbindEvents()
		this.matchMedia.destroy()
	}
}

// -----------------------------------------------------------------------------
export default class extends Component {
	prepare() {
		this.headerPanels = new HeaderPanels({ el: this.element })
	}

	mount() {}

	unmount() {
		if (this.headerPanels) {
			this.headerPanels.destroy()
			this.headerPanels = null
		}
	}
}
